import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, filter, map, Observable, skip, take, tap } from 'rxjs';
import { GenericRequest } from '@app/core/models/request-status.model';
import { Cart } from '@b2b/features/cart/utilities/cart.model';
import { LocalStorageService } from '@shared/tools/local-storage.service';
import { CartItem } from '@b2b/features/cart/utilities/cart.model';
import {
  addCartItem,
  deleteCart,
  deleteCartItem,
  getCart,
  getCartSuccess,
  getLatestCart,
  getMiniCart,
  getOrCreateCart,
  saveCart,
  updateCart,
  updateCartItem,
} from '@b2b/features/cart/core/store/cart.actions';
import { selectCart } from '@b2b/features/cart/core/store/cart.states';
import { CartConnector } from '@b2b/features/cart/core/cart.connector';
import { ToasterService } from '@shared/components/toaster/toaster.service';
import { TOASTER_TYPE } from '@shared/components/toaster/toaster.model';
import { UserService } from '@app/core/user/user.service';
import { CART_ID_KEY } from '@shared/tools/constants';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  #accordionState = new BehaviorSubject(new Map<string, boolean>());

  constructor(
    private store: Store,
    private localStorage: LocalStorageService,
    private cartConnector: CartConnector,
    private toasterService: ToasterService,
    private userService: UserService,
  ) {}

  get accordionState$() {
    return this.#accordionState.asObservable();
  }

  updateAccordionState(state: Map<string, boolean>) {
    this.#accordionState.next(state);
  }

  getCartID() {
    return this.localStorage.getItem(CART_ID_KEY) || '';
  }

  setCartID(cartID: string) {
    return this.localStorage.setItem(CART_ID_KEY, cartID);
  }

  getCart() {
    return this.store.select(selectCart);
  }

  getOrCreateCart() {
    this.store.dispatch(getOrCreateCart());
  }

  takeCart() {
    return this.store.select(selectCart).pipe(
      filter(state => !state.isPristine && !state.isLoading && !!state.data),
      map(state => state.data!),
    );
  }

  getLatestCart() {
    this.userService
      .get()
      .pipe(
        filter(user => !!user.isAuthenticated),
        take(1),
      )
      .subscribe(user => {
        this.store.dispatch(getLatestCart({ customerId: user.username }));
      });
  }

  fetchCart(cartID: string) {
    this.store.dispatch(getCart({ cartID }));
  }

  fetchMiniCart(cartID: string) {
    this.store.dispatch(getMiniCart({ cartID }));
  }

  addCartItem(item: CartItem) {
    this.store.dispatch(addCartItem({ item }));
    return this.store.select(selectCart).pipe(skip(1));
  }

  updateCartItem(entryNumber: number, item: CartItem) {
    this.store.dispatch(updateCartItem({ entryNumber, item }));
    return this.store.select(selectCart).pipe(skip(1));
  }

  updateCart(name: string) {
    this.store.dispatch(updateCart({ name }));
    return this.store.select(selectCart).pipe(skip(1));
  }

  delete(): void {
    const cartCode = this.getCartID();

    this.store.dispatch(deleteCart({ cartCode }));
  }

  deleteCartItem(entryNumber: number, cartCode: string): void {
    this.store.dispatch(deleteCartItem({ entryNumber, cartCode }));
  }

  getCartList({ page = 1 } = {}, customerId: string, b2bUnitId: string) {
    return this.cartConnector.getCartLists(page, customerId, b2bUnitId).pipe(
      map(response => {
        if (response.hasError) {
          return this.toasterService.init({
            id: 'toaster-add-to-cart-fail',
            title: response.message,
            type: TOASTER_TYPE.DANGER,
            content: '',
          });
        }

        return {
          items: response.data?.items.map(item => ({
            ...item,
            updatedAt: new Date(item.updatedAt),
          })),
          pagination: response.data?.pagination,
        };
      }),
    );
  }

  createCart() {
    this.store.dispatch(saveCart({ name: '' }));
    return this.store.select(selectCart).pipe(skip(1));
  }

  applyCoupon(couponCode: string): Observable<GenericRequest<Cart>> {
    const cartCode = this.getCartID();

    return this.cartConnector.applyCoupon(cartCode, couponCode).pipe(
      tap(result => {
        if (!result.hasError && result.data) {
          this.store.dispatch(getCartSuccess({ data: result.data }));
        }
      }),
    );
  }

  removeCoupon(): Observable<GenericRequest<Cart>> {
    const cartCode = this.getCartID();

    return this.cartConnector.removeCoupon(cartCode).pipe(
      tap(result => {
        if (!result.hasError && result.data) {
          this.store.dispatch(getCartSuccess({ data: result.data }));
        }
      }),
    );
  }
}
